import { useState } from "react";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
    androidColorToCss,
    isValidAndroidColor,
    joinAndroidColor,
    splitAndroidColor,
} from "./androidColor";

// Checkerboard shown behind translucent colors so alpha is visible.
const CHECKERBOARD =
    "repeating-conic-gradient(#c8c8c8 0% 25%, #ffffff 0% 50%) 50% / 10px 10px";

interface ColorFieldProps {
    value: string;
    onChange: (next: string) => void;
    /** Width of the text input (px). */
    inputWidth?: number;
}

/**
 * Edits a single Android ARGB color (#AARRGGBB). Shows a color swatch plus a
 * text field for editing the raw string; clicking the swatch opens a picker
 * (native RGB picker + alpha slider).
 */
const ColorField = ({ value, onChange, inputWidth = 170 }: ColorFieldProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const css = androidColorToCss(value);
    const valid = isValidAndroidColor(value);
    const { alpha, rgb } = splitAndroidColor(value);

    const setRgb = (nextRgb: string) => onChange(joinAndroidColor(alpha, nextRgb));
    const setAlpha = (nextAlpha: number) => onChange(joinAndroidColor(nextAlpha, rgb));

    const swatch = (size: number) => (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                background: CHECKERBOARD,
                position: "relative",
                overflow: "hidden",
                flexShrink: 0,
            }}
        >
            <Box sx={{ position: "absolute", inset: 0, background: css ?? "transparent" }} />
        </Box>
    );

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Box
                role="button"
                tabIndex={0}
                aria-label="Open color picker"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setAnchorEl(e.currentTarget);
                }}
                sx={{ cursor: "pointer", display: "inline-flex" }}
            >
                {swatch(28)}
            </Box>

            <TextField
                size="small"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                error={value.length > 0 && !valid}
                sx={{ width: inputWidth }}
                slotProps={{ htmlInput: { style: { fontFamily: "monospace" } } }}
            />

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box sx={{ p: 2, width: 260 }}>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <input
                                type="color"
                                value={rgb.toLowerCase()}
                                onChange={(e) => setRgb(e.target.value)}
                                style={{
                                    width: 48,
                                    height: 48,
                                    padding: 0,
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                }}
                                aria-label="RGB color"
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Alpha ({alpha})
                                </Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={255}
                                    value={alpha}
                                    onChange={(_, v) => setAlpha(v as number)}
                                    aria-label="Alpha"
                                />
                            </Box>
                        </Stack>

                        <TextField
                            size="small"
                            label="Value"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            error={value.length > 0 && !valid}
                            helperText={value.length > 0 && !valid ? "Expected #AARRGGBB" : " "}
                            slotProps={{ htmlInput: { style: { fontFamily: "monospace" } } }}
                        />

                        {swatch(36)}
                    </Stack>
                </Box>
            </Popover>
        </Stack>
    );
};

export default ColorField;
